// src/components/assets/AssetForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetsService } from '../../services/assets';
import { edgeNodesService } from '../../services/edge-nodes';
import Loading from '../common/Loading';

const AssetForm = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { assetId } = useParams();
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [edgeNodes, setEdgeNodes] = useState([]);
  const [protocols, setProtocols] = useState(['']);
  const [customTags, setCustomTags] = useState([{ key: '', value: '' }]);
  
  const [formData, setFormData] = useState({
    asset_name: '',
    asset_type: '',
    vendor: '',
    model_number: '',
    firmware_version: '',
    ip_address: '',
    mac_address: '',
    location: '',
    celerway_edge_node_id: '',
    protocols_used: [],
    criticality_level: 'Medium',
    discovered_by: 'ManualEntry',
    custom_tags: {}
  });

  // Fetch edge nodes for dropdown
  useEffect(() => {
    const fetchEdgeNodes = async () => {
      try {
        const data = await edgeNodesService.getEdgeNodes();
        setEdgeNodes(data);
      } catch (err) {
        console.error('Error fetching edge nodes:', err);
      }
    };

    fetchEdgeNodes();
  }, []);

  // If editing, fetch the asset data
  useEffect(() => {
    if (mode === 'edit' && assetId) {
      const fetchAsset = async () => {
        try {
          setLoading(true);
          const data = await assetsService.getAsset(assetId);
          setFormData(data);
          
          // Set protocols array
          if (data.protocols_used && data.protocols_used.length > 0) {
            setProtocols(data.protocols_used);
          }
          
          // Set custom tags
          if (data.custom_tags && Object.keys(data.custom_tags).length > 0) {
            const tagsArray = Object.entries(data.custom_tags).map(([key, value]) => ({ key, value }));
            setCustomTags(tagsArray);
          }
          
          setError(null);
        } catch (err) {
          console.error('Error fetching asset:', err);
          setError('Failed to load asset data. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      fetchAsset();
    }
  }, [mode, assetId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProtocolChange = (index, value) => {
    const updatedProtocols = [...protocols];
    updatedProtocols[index] = value;
    setProtocols(updatedProtocols);
  };

  const addProtocol = () => {
    setProtocols([...protocols, '']);
  };

  const removeProtocol = (index) => {
    const updatedProtocols = protocols.filter((_, i) => i !== index);
    setProtocols(updatedProtocols);
  };

  const handleTagChange = (index, field, value) => {
    const updatedTags = [...customTags];
    updatedTags[index][field] = value;
    setCustomTags(updatedTags);
  };

  const addTag = () => {
    setCustomTags([...customTags, { key: '', value: '' }]);
  };

  const removeTag = (index) => {
    const updatedTags = customTags.filter((_, i) => i !== index);
    setCustomTags(updatedTags);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Process protocols (remove empty values)
      const filteredProtocols = protocols.filter(p => p.trim() !== '');
      
      // Process custom tags (convert to object and remove empty keys)
      const tagsObject = {};
      customTags.forEach(tag => {
        if (tag.key.trim() !== '') {
          tagsObject[tag.key] = tag.value;
        }
      });
      
      // Prepare the data for submission
      const assetData = {
        ...formData,
        protocols_used: filteredProtocols,
        custom_tags: tagsObject
      };
      
      if (mode === 'edit') {
        await assetsService.updateAsset(assetId, assetData);
      } else {
        await assetsService.createAsset(assetData);
      }
      
      // Redirect to assets list on success
      navigate('/assets');
    } catch (err) {
      console.error('Error saving asset:', err);
      setError(`Failed to ${mode === 'edit' ? 'update' : 'create'} asset. Please check your input and try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading text="Loading asset data..." />;
  }

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {mode === 'edit' ? 'Edit Asset' : 'Create New Asset'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {mode === 'edit' 
              ? 'Update the information for this OT asset.' 
              : 'Add a new OT asset to the system for monitoring and protection.'}
          </p>
        </div>
        <div className="mt-5 md:mt-0 md:col-span-2">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-6 gap-6">
              {/* Asset Name */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="asset_name" className="block text-sm font-medium text-gray-700">
                  Asset Name *
                </label>
                <input
                  type="text"
                  name="asset_name"
                  id="asset_name"
                  required
                  value={formData.asset_name}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* Asset Type */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="asset_type" className="block text-sm font-medium text-gray-700">
                  Asset Type *
                </label>
                <select
                  id="asset_type"
                  name="asset_type"
                  required
                  value={formData.asset_type}
                  onChange={handleChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select type</option>
                  <option value="PLC">PLC</option>
                  <option value="RTU">RTU</option>
                  <option value="HMI">HMI</option>
                  <option value="Sensor">Sensor</option>
                  <option value="Controller">Controller</option>
                  <option value="Gateway">Gateway</option>
                  <option value="SCADA">SCADA System</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Vendor */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="vendor" className="block text-sm font-medium text-gray-700">
                  Vendor
                </label>
                <input
                  type="text"
                  name="vendor"
                  id="vendor"
                  value={formData.vendor || ''}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* Model Number */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="model_number" className="block text-sm font-medium text-gray-700">
                  Model Number
                </label>
                <input
                  type="text"
                  name="model_number"
                  id="model_number"
                  value={formData.model_number || ''}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* Firmware Version */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="firmware_version" className="block text-sm font-medium text-gray-700">
                  Firmware Version
                </label>
                <input
                  type="text"
                  name="firmware_version"
                  id="firmware_version"
                  value={formData.firmware_version || ''}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* IP Address */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="ip_address" className="block text-sm font-medium text-gray-700">
                  IP Address
                </label>
                <input
                  type="text"
                  name="ip_address"
                  id="ip_address"
                  value={formData.ip_address || ''}
                  onChange={handleChange}
                  placeholder="e.g. 192.168.1.100"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* MAC Address */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="mac_address" className="block text-sm font-medium text-gray-700">
                  MAC Address
                </label>
                <input
                  type="text"
                  name="mac_address"
                  id="mac_address"
                  value={formData.mac_address || ''}
                  onChange={handleChange}
                  placeholder="e.g. 00:1A:2B:3C:4D:5E"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* Location */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  id="location"
                  value={formData.location || ''}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* Celerway Edge Node */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="celerway_edge_node_id" className="block text-sm font-medium text-gray-700">
                  Celerway Edge Node
                </label>
                <select
                  id="celerway_edge_node_id"
                  name="celerway_edge_node_id"
                  value={formData.celerway_edge_node_id || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">None</option>
                  {edgeNodes.map(node => (
                    <option key={node.edge_node_id} value={node.edge_node_id}>
                      {node.hostname || node.edge_node_id} {node.status === 'Online' ? '(Online)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Criticality Level */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="criticality_level" className="block text-sm font-medium text-gray-700">
                  Criticality Level
                </label>
                <select
                  id="criticality_level"
                  name="criticality_level"
                  value={formData.criticality_level || 'Medium'}
                  onChange={handleChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              {/* Protocols Used */}
              <div className="col-span-6">
                <label className="block text-sm font-medium text-gray-700">
                  Protocols Used
                </label>
                <div className="mt-1 space-y-2">
                  {protocols.map((protocol, index) => (
                    <div key={index} className="flex items-center">
                      <input
                        type="text"
                        value={protocol}
                        onChange={(e) => handleProtocolChange(index, e.target.value)}
                        placeholder="e.g. Modbus TCP, OPC UA, etc."
                        className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removeProtocol(index)}
                        className="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addProtocol}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Protocol
                  </button>
                </div>
              </div>

              {/* Custom Tags */}
              <div className="col-span-6">
                <label className="block text-sm font-medium text-gray-700">
                  Custom Tags
                </label>
                <div className="mt-1 space-y-2">
                  {customTags.map((tag, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={tag.key}
                        onChange={(e) => handleTagChange(index, 'key', e.target.value)}
                        placeholder="Key"
                        className="focus:ring-blue-500 focus:border-blue-500 block shadow-sm sm:text-sm border-gray-300 rounded-md w-1/3"
                      />
                      <input
                        type="text"
                        value={tag.value}
                        onChange={(e) => handleTagChange(index, 'value', e.target.value)}
                        placeholder="Value"
                        className="focus:ring-blue-500 focus:border-blue-500 block shadow-sm sm:text-sm border-gray-300 rounded-md flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeTag(index)}
                        className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTag}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Tag
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/assets')}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  mode === 'edit' ? 'Update Asset' : 'Create Asset'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssetForm;
